import { useEffect, useState } from 'react';
import { Outlet } from 'react-router';
import TokenManager from '@zeeve-platform/login-utility';
import InAppNotificationsManager from '@zeeve-platform/in-app-notifications-frontend';
import globalConfig from '../config/config';
import { axiosBackendInstance, loadAxiosInstances } from '../utils/axios';
import BaseUrls from '../utils/baseUrls';
import FullSizeLoader from '../components/FullSizeLoader';
import useUserStore from '../store/user';
import API from '../api/routes';

/**
 * custom hook to check every route before rendering
 * @returns JSX.Element
 */
const AuthRoute = (): JSX.Element => {
    /** local state to manage loader */
    const [isLoading, setIsLoading] = useState(true);

    /** global state from user store */
    const setUserDetails = useUserStore((state) => state.setDetails);

    /**
     * to get the user details from application backend by calling API
     */
    const getUserDetails = async (): Promise<void> => {
        if (globalConfig.getConfig?.authEnabled) {
            const userResponse = await axiosBackendInstance.get(
                `${BaseUrls.getPlatformBackendUrl()}/dashboard${API.USER.PROFILE}`,
            );
            if (userResponse.status === 200) {
                setUserDetails(userResponse.data.data);
            }
        }
    };

    /**
     * to initialise the in app notification
     */
    const setupInAppNotifications = () => {
        const token = TokenManager.getInstance().getAccessToken();
        InAppNotificationsManager.createInstance(BaseUrls.getInappNotificationsUrl(token), {
            InaApp: {},
        });
        TokenManager.getInstance().updateCallbacks({
            accessTokenRotationCallbacks: {
                onAccessTokenUpdate: (access_token) => {
                    // update notifications manager ws address with newer token whenever a token is rotated
                    InAppNotificationsManager.getInstance().updateWsAddress(
                        BaseUrls.getInappNotificationsUrl(access_token),
                    );
                },
            },
        });
    };

    /**
     * initialise authentication and set other parameters
     */
    const intialiseAuth = async () => {
        try {
            /** set axios instances for application backend and auth service */
            loadAxiosInstances();
            /** call token manager only when the auth is enabled from config */
            if (globalConfig.getConfig?.authEnabled) {
                await TokenManager.createInstance(
                    BaseUrls.getAuthBackendUrl(),
                    axiosBackendInstance,
                );
                setupInAppNotifications();
            }
            /** fetching general info of the user */
            await getUserDetails();
            // eslint-disable-next-line
        } catch (error) {
            // TODO handle error
            console.error('Authentication error');
        } finally {
            setIsLoading(false);
        }
    };

    /** to run code at the time of component did mount */
    useEffect(() => {
        intialiseAuth();
    }, []);

    return <>{isLoading ? <FullSizeLoader /> : <Outlet />}</>;
};

export default AuthRoute;
